import { Signal } from "@preact/signals-react"
import { isArray, isEmpty, isNil, isObject, isString } from "lodash-es";
import { IActionTrigger, Service } from "../../resolvers-types";
import { convertOpenAPIFieldsToPropertiesEditor, extractFieldList, OutputObject } from "../generators/codeGeneration/openApiParser";
import { getFormData } from "./dataService";
import { isSignal } from "./utils";
import { odataOperators } from "../sampleData/test-harness/odata-query-form";

export interface OpenApiMethod {
  path: string;
  shortPath: string;
  method: string;
  summary: string;
  description: string;
}

export interface OpenApiService {
  serviceName: string;
  methods: OpenApiMethod[];
}

interface IFieldToAdd {
  fieldName: string;
  value: any;
}

interface IHistoryRecord {
  UserId: string,
  Service: string,
  Verb: string,
  Parameters: string
}

export type OpenApiServices = OpenApiService[];

export function findServiceNameInOpenApiServices(
  openApiServices: OpenApiService[],
  serviceName?: string
): OpenApiService | undefined | null {
  if (!serviceName) {
    return null;
  }

  if (serviceName.includes(".")) {
    serviceName = serviceName.split(".")[0]; // Extract the service name before the first dot
  }
  const result = openApiServices.find((service) => service.serviceName === serviceName);

  if (!result) {
    return {
      serviceName: serviceName,
      methods: [],
    };
  }

  return result;
}

const formatFields = (fieldsInput: any[] | string) => {
  let fields = fieldsInput;
  try {
    if (isString(fields)) {
      fields = JSON.parse(fields);
    }
  } catch {
    return "";
  }
  if (!isArray(fields)) {
    return "";
  }

  if (fields.length === 0) {
    return "Empty Fields";
  }
  // remove from fields all the fields that have a null or empty value
  const result = fields
    .filter((field) => {
      if (field.Value === null || (Array.isArray(field.Value) && field.Value.length === 0) || isEmpty(field.Value)) {
        return false;
      }
      return true;
    })
    .map((field) => {
      let str = `${field.Name}: `;
      if (isObject(field.Value) && Array.isArray(field.Value)) {
        // check if items of field value are objects or string
        // if they are objects, join the value of each key
        // if they are strings, join the values
        const isObjectArray = field.Value.every((value) => isObject(value));
        if (isObjectArray) {
          str += field.Value.map((value) => {
            return Object.values(value)
              .map((v) => {
                let v2 = v;
                // get operations for odata
                const op = odataOperators.find((op) => op.Value === v);
                // if exists operation, return the name of the operation
                if (op) v2 = op.Symbol;
                return v2;
              })
              .join(" ");
          }).join(" and ");
        }
        // if they are strings, join the values
        else {
          str += field.Value.join(", ");
        }
      }

      if (isObject(field.Value) && !Array.isArray(field.Value)) {
        str += Object.entries(field.Value)
          .map(([key, value]) => {
            let v = value;
            // get operations for odata
            const op = odataOperators.find((op) => (value as string).includes(op.Value));
            // if exists operation, return the name of the operation
            if (op) v = (value as string).replace(op.Value, op.Symbol);
            return `${key} = ${v}`;
          })
          .join(" and ");
      }

      if (isString(field.Value)) {
        str += `${field.Value}, `;
      }
      return str;
    });

  return result.join(" ;\n");
};

export const getOpenAPIFields = (spec, path) => {
  const s2 = extractFieldList(spec as any, path);
  const s3 = convertOpenAPIFieldsToPropertiesEditor(s2.properties);
  addFieldsToFieldList(
    s3.FieldList,
    [
      { fieldName: "fromTestHarness", value: true },
      { fieldName: "includeCheckbox", value: true },
      { fieldName: "forceBindings", value: true }
    ]);

  if (s2.extraProperties) {
    s3.ExtraFields = convertOpenAPIFieldsToPropertiesEditor(s2.extraProperties, true).FieldList;
    addFieldsToFieldList(
      s3.ExtraFields,
      [
        { fieldName: "fromTestHarness", value: true },
        { fieldName: "includeCheckbox", value: true },
        { fieldName: "forceBindings", value: true }
      ]);
  }

  return s3;
};

type Field = {
  Name: string;
  Value: any;
  Bindings?: {
    Value: string;
  };
  Required?: boolean;
};

type Transformed = {
  [key: string]: any;
  Bindings: { [key: string]: string | null };
  Required: { [key: string]: boolean | null };
};

export function repeaterToFilterFields(repeater) {
  if (isNil(repeater) || !isArray(repeater)) return [];
  return repeater.map((r) => {
    const data = getFormData(r);
    if (data["Bindings"]) {
      data["Bindings"] = { Value: data["Bindings"] };
    }

    delete data["ShowBindings"];
    return data;
  });
}

export function transformFields(fields: Field[]): Transformed {
  const result: any = {};
  const Bindings: { [key: string]: string | null } = {};
  const Required: { [key: string]: boolean | null } = {};

  for (const field of fields) {
    result[field.Name] = field.Value;
    Bindings[field.Name] = field.Bindings ? field.Bindings.Value : null;
    Required[field.Name] =
      typeof field.Required === "boolean" ? field.Required : null;
  }

  result.Bindings = Bindings;
  result.Required = Required;
  return result;
}

function addFieldsToFieldList(s3: OutputObject["FieldList"] | null, fieldsToAdd: IFieldToAdd[]) {
  const fields = (s3?.fields as any[]).map((field: any, idx: number) => {
    fieldsToAdd.forEach(({ fieldName, value }) => {
      field[fieldName] = value;
    });
    return field;
  });
  if (s3 && s3) {
    s3.fields = fields;
  }
  return s3;
}

export const getServiceFieldValues = (service: Service) => {
  if (!service || !service.Fields) return [];
  const Bindings = {};
  const Required = {};
  const result = {};
  service.Fields.forEach((field) => {
    if (!field || !field.Name) return null;
    if (field?.Bindings?.Value) {
      const v = field?.Bindings?.Value as string | Signal<string>;
      Bindings[field.Name] = isSignal(v) ? (v as Signal<string>).value : v;
    }
    if (field?.Required !== undefined) {
      Required[field.Name] = field?.Required;
    }
    result[field.Name] = field?.Value || null;
  });
  if (!isEmpty(Bindings)) {
    result["Bindings"] = Bindings;
  }
  if (!isEmpty(Required)) {
    result["Required"] = Required;
  }
  return result;
}

export function ArrayToValueNameObj(arr: string[]) {
  if (!arr || arr.length === 0 || !Array.isArray(arr)) return [];
  return arr.map((item) => {
    if (isString(item)) {
      const splittedItem = item.split(" ");
      return { Name: splittedItem[0], Value: splittedItem[1] };
    }
    if (isObject(item)) {
      return { Name: item["Name"], Value: item["Value"] };
    }
  });
}

export function transformAggregateIntoState(AggObjt: { [key: string]: string[] }) {
  if (!AggObjt || isEmpty(AggObjt)) return [];
  const result: any[] = [];
  Object.entries(AggObjt).forEach(([key, value]) => {
    value.forEach((val) => {
      result.push({ AggOperator: key.toLowerCase(), AggField: val });
    });
  });
  return result;
}

interface FieldsItem {
  Name: string;
  [key: string]: any;
}

function addOrReplaceFields(arr: FieldsItem[] | null, value: any, key: string) {
  if (!arr) return [{ Name: key, Value: value }];
  const index = arr.findIndex((item) => item.Name === key);
  if (index !== -1) {
    arr[index].Value = value;
  } else {
    arr.push({ Name: key, Value: value });
  }
  return arr;
}

export function generateAggregateObject(aggregateArray: any[] | null) {
  return aggregateArray
    ? aggregateArray?.reduce((acc, agg) => {
      if (isEmpty(agg)) {
        return acc;
      }
      acc[agg.AggOperator.charAt(0).toUpperCase() + agg.AggOperator.slice(1)] =
        acc[agg.AggOperator.charAt(0).toUpperCase() + agg.AggOperator.slice(1)] || [];
      acc[agg.AggOperator.charAt(0).toUpperCase() + agg.AggOperator.slice(1)].push(agg.AggField);
      return acc;
    }, {})
    : null;
}

function generateBindingsTypeData(service: Service) {
  if (!service || !service.Bindings) return { __typename: 'SetState', Bindings: {} };
  return { __typename: 'SetState', Bindings: service.Bindings || {} };
}

function generateServiceFromHistory(historyRecord: IHistoryRecord) {
  if (!historyRecord) return {};
  const verb = historyRecord.Verb.toLowerCase().split(" ").map((v, i) => {
    if (i === 0) return v;
    if (i === 1) return `.${v}`;
    return `-${v}`;
  }).join("");
  const serviceName = `${historyRecord.Service}.${verb}`.toLowerCase();

  const service: Service = {
    Name: serviceName,
    Id: serviceName,
    Service: serviceName,
    Fields: JSON.parse(historyRecord.Parameters),
  };
  return service;

}

function getUpdatedActionsAfterAdd(serviceName: string, commandId: string, actions: IActionTrigger[]) {
  if (!actions || !Array.isArray(actions)) return [];

  let commandIndex = -1;
  const actionIdx = actions.findIndex((a) => {
    commandIndex = a.CommandSet?.Commands?.findIndex((c) => c?.Id === commandId) ?? -1;
    return commandIndex > -1;
  });
  const newActions = [...actions];
  if (actionIdx > -1 && commandIndex > -1) {
    const action = newActions[actionIdx];
    if (action && action.CommandSet && action.CommandSet.Commands) {
      const command = action.CommandSet.Commands[commandIndex];
      if (command) {
        if (command.Parameters?.length) {
          command.Parameters?.forEach((param, idx) => {
            if (param && param.Name === "ServiceName") {
              command.Parameters![idx] = { ...param, Value: serviceName };
            }
          });
        } else {
          command.Parameters = [{ Name: "ServiceName", Value: serviceName }];
        }
        newActions[actionIdx] = action;
        return newActions;
      }
    }
  }
  return newActions;
}

function addODataExtraFields(fields?: Service["Fields"], ExtraFields?: Service["Fields"]) {
  if (!fields && !ExtraFields) return [];
  const arr = fields || [];
  ExtraFields?.forEach((xf) => {
    const index = arr.findIndex((item) => item?.Name === xf?.Name);
    if (index !== -1) {
      arr[index] = xf;
    } else {
      arr.push(xf);
    }
  });
  return arr;
}

function transformServiceGet(service: Service | null, Id: string | null, selectedVerb: string | null): Service | null {
  if (!service || !Id) return null;

  const fields = [{ Name: "Id", Value: Id }];

  let newService = service.Service;
  if (selectedVerb) {
    newService = service.Service?.replace(selectedVerb, 'get');
  }

  if (service.Service === service.Name) {

  }

  return {
    ...service,
    ...(service.Service === service.Name ? { ...service, Name: newService, Id: newService } : {}),
    Service: newService,
    Fields: fields,
  };
}

function sliceFromRepeater(repeaterForm: any, index: number) {
  if (!repeaterForm || !Array.isArray(repeaterForm) || index < 0 || index >= repeaterForm.length) {
    return [];
  }
  const newForm = repeaterForm.slice(0, index).concat(repeaterForm.slice(index + 1));
  return newForm;
}

export const interpreterFunctions = {
  findServiceNameInOpenApiServices,
  getOpenAPIFields,
  transformFields,
  addOrReplaceFields,
  generateAggregateObject,
  repeaterToFilterFields,
  transformAggregateIntoState,
  generateBindingsTypeData,
  formatFields,
  generateServiceFromHistory,
  getUpdatedActionsAfterAdd,
  addODataExtraFields,
  transformServiceGet,
  sliceFromRepeater
};

